import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

type User = {
  id: number
  name: string
  email: string
  user_type: 'member' | 'admin'
  created_at: string
  clubs_count: number
  book_picks_count: number
}

type Club = {
  id: number
  name: string
  created_at: string
  members_count: number
  book_picks_count: number
  current_season: {
    id: number
    season_number: number
    status: string
  } | null
  invite_token: string
}

type Props = {
  user: {
    id: number
    name: string
    email: string
    user_type?: 'member' | 'admin'
  } | null
  onBack: () => void
  onImpersonationStart: () => void
}

export default function Admin({ user, onBack, onImpersonationStart }: Props) {
  const [activeTab, setActiveTab] = useState<'users' | 'clubs'>('users')
  const [users, setUsers] = useState<User[]>([])
  const [clubs, setClubs] = useState<Club[]>([])
  const [loading, setLoading] = useState(true)
  const [impersonating, setImpersonating] = useState<number | null>(null)
  const navigate = useNavigate()

  // Redirect if not admin
  if (!user || user.user_type !== 'admin') {
    navigate('/dashboard')
    return null
  }

  useEffect(() => {
    fetchData()
  }, [activeTab])

  const fetchData = async () => {
    setLoading(true)
    try {
      if (activeTab === 'users') {
        const response = await fetch('/api/v1/admin/users')
        const data = await response.json()
        setUsers(data.users)
      } else {
        const response = await fetch('/api/v1/admin/clubs')
        const data = await response.json()
        setClubs(data.clubs)
      }
    } catch (err) {
      console.error('Error fetching admin data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleImpersonate = async (userId: number, userName: string) => {
    const reason = prompt(`Why are you impersonating ${userName}?`)
    if (!reason || reason.trim() === '') {
      alert('Reason is required')
      return
    }

    setImpersonating(userId)
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')
      const response = await fetchWithCsrf('/api/v1/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, reason: reason.trim() })
      })

      if (response.ok) {
        onImpersonationStart()
      } else {
        const error = await response.json()
        alert(error.error || 'Failed to start impersonation')
      }
    } catch (err) {
      console.error('Error starting impersonation:', err)
      alert('Failed to start impersonation')
    } finally {
      setImpersonating(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage users and clubs
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`${
                activeTab === 'users'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('clubs')}
              className={`${
                activeTab === 'clubs'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Clubs ({clubs.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'users' && (
              <UsersList
                users={users}
                onImpersonate={handleImpersonate}
                impersonating={impersonating}
              />
            )}
            {activeTab === 'clubs' && <ClubsList clubs={clubs} />}
          </>
        )}
      </div>
    </div>
  )
}

function UsersList({ users, onImpersonate, impersonating }: {
  users: User[]
  onImpersonate: (userId: number, userName: string) => void
  impersonating: number | null
}) {
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <ul className="divide-y divide-gray-200">
        {users.map((user) => (
          <li key={user.id}>
            <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <p className="text-sm font-medium text-indigo-600 truncate">
                      {user.name}
                    </p>
                    {user.user_type === 'admin' && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <span>{user.email}</span>
                    <span className="mx-2">•</span>
                    <span>{user.clubs_count} clubs</span>
                    <span className="mx-2">•</span>
                    <span>{user.book_picks_count} picks</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    Joined {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
                {user.user_type !== 'admin' && (
                  <button
                    onClick={() => onImpersonate(user.id, user.name)}
                    disabled={impersonating !== null}
                    className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {impersonating === user.id ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Impersonating...
                      </span>
                    ) : (
                      'Impersonate'
                    )}
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ClubsList({ clubs }: { clubs: Club[] }) {
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <ul className="divide-y divide-gray-200">
        {clubs.map((club) => (
          <li key={club.id}>
            <div className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-indigo-600 truncate">
                    {club.name}
                  </p>
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <span>{club.members_count} members</span>
                    <span className="mx-2">•</span>
                    <span>{club.book_picks_count} books</span>
                    {club.current_season && (
                      <>
                        <span className="mx-2">•</span>
                        <span>Season {club.current_season.season_number} ({club.current_season.status})</span>
                      </>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    Created {new Date(club.created_at).toLocaleDateString()}
                  </p>
                  <p className="mt-1 text-xs text-gray-400 font-mono">
                    Invite: {club.invite_token}
                  </p>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
